import { ChevronRight, Sparkles, X } from "lucide-react";
import { useMemo, useState } from "react";
import { motion } from "framer-motion";

import { DiscoveryCard } from "@/components/karma/DiscoveryCard";
import { SpiritualCoach } from "@/components/karma/SpiritualCoach";
import { UserCard } from "@/components/karma/UserCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

import { useToast } from "@/hooks/use-toast";
import { getAge } from "@/lib/karma/age";
import { getDailyGuidance } from "@/lib/karma/daily";
import { computeCoachTodaySummary } from "@/lib/karma/coach";
import { computeKarmaNumerology } from "@/lib/karma/numerology";
import { computeMatchOverview, personalizeMatchOverview } from "@/lib/karma/match";
import {
  ensureStore,
  getMe,
  listMatchesForUser,
  listUsers,
  markPassed,
  toggleLike,
} from "@/lib/karma/storage";

export default function KarmaHome() {
  const { toast } = useToast();
  const [storeVersion, setStoreVersion] = useState(0);
  const [cursor, setCursor] = useState(0);

  const store = useMemo(() => {
    storeVersion;
    return ensureStore();
  }, [storeVersion]);

  const me = getMe(store);
  const users = listUsers(store);

  const meNumerology = useMemo(
    () => computeKarmaNumerology(me.displayName, me.birthDate),
    [me.displayName, me.birthDate],
  );

  const daily = useMemo(() => getDailyGuidance(new Date()), []);

  const candidates = useMemo(() => {
    if (!me.matchEnabled) return [];

    const minAge = me.prefMinAge ?? 18;
    const maxAge = me.prefMaxAge ?? 99;
    const onlyMatchEnabled = me.prefOnlyMatchEnabled ?? false;

    const destinySet = new Set(me.prefDestinyNumbers ?? []);
    const heartSet = new Set(me.prefHeartDesireNumbers ?? []);
    const expressionSet = new Set(me.prefExpressionNumbers ?? []);
    const karmicSet = new Set(me.prefKarmicDebtNumbers ?? []);
    const useStatusFilter = me.prefOnlySimilarStatus ?? false;

    const myStatus = computeCoachTodaySummary(me, new Date()).level.level;

    const blocked = new Set(me.blockedUserIds ?? []);
    const passed = new Set(me.passedUserIds ?? []);

    const base = users
      .filter((u) => u.id !== me.id)
      .filter((u) => !blocked.has(u.id))
      .filter((u) => !(u.blockedUserIds ?? []).includes(me.id))
      .filter((u) => !passed.has(u.id))
      .filter((u) => {
        const age = getAge(u.birthDate);
        return age >= minAge && age <= maxAge;
      })
      .filter((u) => (onlyMatchEnabled ? u.matchEnabled : true));

    return base
      .map((u) => {
        const nu = computeKarmaNumerology(u.displayName, u.birthDate);

        if (destinySet.size && !destinySet.has(nu.destinyNumber)) return null;
        if (heartSet.size && !heartSet.has(nu.heartDesireNumber)) return null;
        if (expressionSet.size && !expressionSet.has(nu.expressionNumber)) return null;
        if (karmicSet.size && !nu.karmicCodes.some((c) => karmicSet.has(c))) return null;

        if (useStatusFilter) {
          const otherStatus = computeCoachTodaySummary(u, new Date()).level.level;
          if (Math.abs(otherStatus - myStatus) > 1) return null;
        }

        const overview = computeMatchOverview(meNumerology, nu);
        return {
          user: u,
          overview: personalizeMatchOverview(overview, {
            meName: me.displayName,
            otherName: u.displayName,
          }),
        };
      })
      .filter(Boolean)
      .sort((a, b) => b.overview.scores.dailyHarmony - a.overview.scores.dailyHarmony);
  }, [users, me.id, me.blockedUserIds, me.passedUserIds, me.prefMinAge, me.prefMaxAge, me.prefOnlyMatchEnabled, me.prefDestinyNumbers, me.prefHeartDesireNumbers, me.prefExpressionNumbers, me.prefKarmicDebtNumbers, me.prefOnlySimilarStatus, meNumerology, me.matchEnabled, me.displayName, me.coach]);

  const current = candidates.length ? candidates[cursor % candidates.length] : null;

  const likeAndAdvance = (userId: string) => {
    const before = ensureStore();
    const beforeMatches = listMatchesForUser(me.id, before);

    const nextStore = toggleLike(userId);
    const afterMatches = listMatchesForUser(me.id, nextStore);

    if (afterMatches.length > beforeMatches.length) {
      toast({
        title: "Yeni eşleşme!",
        description: "Karşılıklı beğeni oldu. Mesajlar sekmesinden sohbet başlatabilirsin.",
      });
    }

    setStoreVersion((v) => v + 1);
    setCursor((v) => v + 1);
  };

  const passAndAdvance = (userId: string) => {
    markPassed(userId);
    setStoreVersion((v) => v + 1);
    setCursor((v) => v + 1);
  };

  const topPicks = useMemo(() => {
    const base = candidates.slice(0, 12);
    return current ? base.filter((c) => c.user.id !== current.user.id).slice(0, 8) : base.slice(0, 8);
  }, [candidates, current]);

  if (!me.matchEnabled) {
    return (
      <SpiritualCoach
        me={me}
        numerology={meNumerology}
        onStoreChange={() => setStoreVersion((v) => v + 1)}
      />
    );
  }

  return (
    <div className="w-full">
      <div className="grid gap-6 lg:grid-cols-[minmax(0,460px)_1fr] lg:items-start">
        <div className="space-y-4">
          <Card className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
              <Sparkles className="h-4 w-4" />
              Bugünün enerjisi {daily.number}: {daily.title}
            </div>
            <div className="mt-2 text-sm text-muted-foreground line-clamp-2">{daily.focus}</div>
          </div>
          <Button
            variant="outline"
            className="shrink-0 rounded-full"
            onClick={() => setCursor((v) => v + 1)}
          >
            Sonraki <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
          </Card>

          {current ? (
            <div className="space-y-3">
              <motion.div
                key={current.user.id}
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.15}
                onDragEnd={(_, info) => {
                  if (info.offset.x > 120) {
                    likeAndAdvance(current.user.id);
                    return;
                  }
                  if (info.offset.x < -120) {
                    passAndAdvance(current.user.id);
                    return;
                  }
                }}
                className="mx-auto w-full max-w-[460px] touch-pan-y"
              >
                <DiscoveryCard
                  user={current.user}
                  overview={current.overview}
                  onLike={() => likeAndAdvance(current.user.id)}
                />
              </motion.div>

              <div className="mx-auto flex w-full max-w-[460px] items-center justify-between gap-3">
                <Button
                  variant="outline"
                  className="flex-1 rounded-full"
                  onClick={() => passAndAdvance(current.user.id)}
                >
                  <X className="mr-2 h-4 w-4" /> Geç
                </Button>
                <Button
                  className="flex-1 rounded-full"
                  onClick={() => likeAndAdvance(current.user.id)}
                >
                  Beğen
                </Button>
              </div>

              <div className="mx-auto w-full max-w-[460px] text-center text-xs text-muted-foreground">
                Kaydır: sağa beğen • sola geç
              </div>
            </div>
          ) : (
            <Card className="p-5 text-sm text-muted-foreground">Keşfedecek profil yok.</Card>
          )}

          <Card className="p-4">
            <div className="text-sm font-semibold">İpucu</div>
            <div className="mt-1 text-sm text-muted-foreground">
              Profil &gt; Panel bölümünden adını, doğum tarihini ve fotoğrafını güncellersen eşleşmelerin daha iyi olur.
            </div>
          </Card>
        </div>

        <aside className="space-y-4">
          <Card className="p-5">
            <div className="font-display text-lg font-semibold tracking-tight">Günlük rehber</div>
            <div className="mt-1 text-sm text-muted-foreground">{daily.title}</div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl border bg-muted/30 p-4">
                <div className="text-sm font-semibold">Odak</div>
                <div className="mt-1 text-sm text-muted-foreground">{daily.focus}</div>
              </div>
              <div className="rounded-2xl border bg-muted/30 p-4">
                <div className="text-sm font-semibold">Meditasyon</div>
                <div className="mt-1 text-sm text-muted-foreground">{daily.meditation}</div>
              </div>
              <div className="rounded-2xl border bg-muted/30 p-4">
                <div className="text-sm font-semibold">Olumlama</div>
                <div className="mt-1 text-sm text-muted-foreground">{daily.affirmation}</div>
              </div>
              <div className="rounded-2xl border bg-muted/30 p-4">
                <div className="text-sm font-semibold">Küçük adım</div>
                <div className="mt-1 text-sm text-muted-foreground">{daily.action}</div>
              </div>
            </div>
          </Card>

          <Card className="p-5">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="font-display text-lg font-semibold tracking-tight">Önerilen profiller</div>
                <div className="mt-1 text-sm text-muted-foreground">Uyuma göre sıralı kısa liste.</div>
              </div>
              <Button
                variant="outline"
                className="shrink-0 rounded-full"
                onClick={() => setCursor((v) => v + 1)}
              >
                Yenile
              </Button>
            </div>

            {topPicks.length ? (
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {topPicks.map(({ user }) => (
                  <UserCard
                    key={user.id}
                    user={user}
                    showMatchBadge
                    onLike={() => {
                      toggleLike(user.id);
                      setStoreVersion((v) => v + 1);
                    }}
                  />
                ))}
              </div>
            ) : (
              <div className="mt-4 text-sm text-muted-foreground">Henüz öneri yok.</div>
            )}
          </Card>
        </aside>
      </div>
    </div>
  );
}
